/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { getListingVariants } from './getListingVariants';
import getListingProducts from './helpers/getListingProducts';
import { getProductChannel, getListingChannelId } from './helpers/channelHelpers';
import resolveSalesChannelId from './helpers/resolveSalesChannelId';
import { showDraftForChannelToast } from './helpers/showDraftForChannelToast';
import {
  buildCreateDraftMediaForChannel,
  buildListingMediaPayload,
  resolveMediaMasterVariantIds,
  shouldIncludeDraftMedia,
} from './helpers/buildDraftMediaPayload';
import refreshListingAssetsForChannels from './helpers/refreshListingAssetsForChannels';
import { stageListingProductsFromCreateDraft } from './stageListingProductsFromCreateDraft';

export const createProductDraftFromMaster = createAsyncThunk(
  'products/createProductDraftFromMaster',
  async ({
    productId,
    channelIds,
    masterVariantIds,
    draftMedia = {},
    overrideExisting = false,
    productOnly = false,
    suppressToast = false,
  }, { dispatch, getState, extra: { sdk, Toast } }) => {
    const state = getState();
    const product = state.products.items.find(({ _id }) => _id === productId);
    const channelIdsWithoutListing = [];
    const channelIdsWithListing = [];

    channelIds.forEach((channelId) => {
      const listingProductId = getProductChannel(product, channelId, state.products)?.listingProduct?._id;
      if (listingProductId) {
        channelIdsWithListing.push({ channelId, listingProductId });
      } else {
        channelIdsWithoutListing.push(channelId);
      }
    });

    const listingProducts = [];
    const includeDraftMedia = shouldIncludeDraftMedia(draftMedia.publishingScope);
    const includeGallery = includeDraftMedia && !draftMedia.isVariantSync;
    const mediaMasterVariantIds = includeDraftMedia
      ? resolveMediaMasterVariantIds({
        masterVariantIds,
        product,
        variantMediaByChannel: draftMedia.variantMediaByChannel,
        variantMediaStepCompleted: draftMedia.variantMediaStepCompleted,
      })
      : null;

    if (channelIdsWithoutListing.length) {
      const { user: { team } } = state;
      const salesChannelIds = channelIdsWithoutListing.map(
        (channelId) => resolveSalesChannelId(channelId, team?.salesChannels),
      );
      const listingMedia = includeDraftMedia
        ? buildListingMediaPayload({
          channelIds: channelIdsWithoutListing,
          productMediaByChannel: draftMedia.productMediaByChannel,
          variantMediaByChannel: draftMedia.variantMediaByChannel,
          mediaAltTextByChannel: draftMedia.mediaAltTextByChannel,
          salesChannels: team?.salesChannels,
          masterVariantIds: mediaMasterVariantIds,
          includeGallery,
        })
        : [];
      const { data } = await sdk.products.createListings({
        masterProductId: productId,
        salesChannelIds,
        ...(masterVariantIds?.length && { masterVariantIds }),
        ...(listingMedia.length && { listingMedia }),
      });

      const createdListingProducts = getListingProducts({
        state: getState(),
        productId,
        channelIds: channelIdsWithoutListing,
        createdListings: data.createdListings || [],
      });

      if (!createdListingProducts.length) {
        throw new Error('Staging products not created');
      }

      const createdListingProductsPayload = createdListingProducts.map((listing) => ({
        channelId: getListingChannelId(listing),
        listingProduct: { ...listing },
        variants: [],
        fromCreateDraft: false,
      }));

      dispatch(stageListingProductsFromCreateDraft({
        productId,
        listingProducts: createdListingProductsPayload,
      }));

      await Promise.all(createdListingProducts.map((listing) => dispatch(getListingVariants({
        masterProductId: productId,
        channelId: getListingChannelId(listing),
        listingProductId: listing._id,
      })).unwrap()));

      listingProducts.push(...createdListingProductsPayload);
    }

    if (channelIdsWithListing.length) {
      const createDraftResults = await Promise.all(
        channelIdsWithListing.map(async ({ channelId, listingProductId }) => {
          const mediaPayload = includeDraftMedia
            ? buildCreateDraftMediaForChannel({
              channelId,
              productMediaByChannel: draftMedia.productMediaByChannel,
              variantMediaByChannel: draftMedia.variantMediaByChannel,
              mediaAltTextByChannel: draftMedia.mediaAltTextByChannel,
              masterVariantIds: mediaMasterVariantIds,
              includeGallery,
            })
            : {};
          const { data } = await sdk.products.createDraft(listingProductId, {
            source: 'master',
            ...(masterVariantIds?.length && { masterVariantIds }),
            ...(productOnly && { productOnly: true }),
            ...mediaPayload,
            ...(overrideExisting && includeDraftMedia && { replaceExistingDraftAssets: true }),
          });

          await dispatch(getListingVariants({
            masterProductId: productId,
            channelId,
            listingProductId: data.listing._id,
          })).unwrap();

          return {
            channelId,
            listingProduct: data.listing,
            variants: data.variants || [],
            fromCreateDraft: true,
          };
        }),
      );

      listingProducts.push(...createDraftResults);
    }

    if (!listingProducts.length) {
      throw new Error('Staging products not created');
    }

    const { user: { team } } = getState();

    dispatch(stageListingProductsFromCreateDraft({ productId, listingProducts }));

    const listings = listingProducts.map(({ channelId, listingProduct }) => ({
      channelId,
      listingProductId: listingProduct._id,
    }));

    await refreshListingAssetsForChannels({
      dispatch,
      getState,
      productId,
      listings,
    });

    if (!suppressToast) {
      showDraftForChannelToast({
        Toast,
        channelIds,
        salesChannels: team?.salesChannels || [],
      });
    }

    return { productId, listingProducts };
  },
);

export const reducer = (builder) => {
  builder
    .addCase(
      createProductDraftFromMaster.pending, (state) => {
        state.isBusy = true;
        state.error = null;
      },
    )
    .addCase(
      createProductDraftFromMaster.fulfilled, (state) => {
        state.isBusy = false;
      },
    )
    .addCase(createProductDraftFromMaster.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot create listing from master';
    });
};
